import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { halamanAwal } from "@/lib/roles";

const OTP_TTL_SECONDS = 5 * 60;
const RESEND_COOLDOWN_SECONDS = 30;

export default function VerifyOtp() {
    const { verifyOtp, resendOtp } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    // otpToken dibawa lewat location.state (bukan localStorage) -- kalau halaman ini
    // diakses langsung tanpa lewat /login, tidak ada sesi tertunda utk diverifikasi.
    const [otpToken, setOtpToken] = useState(location.state?.otpToken || null);
    const email = location.state?.email || "";

    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(OTP_TTL_SECONDS);
    const [cooldown, setCooldown] = useState(0);
    const inputRef = useRef(null);

    useEffect(() => { inputRef.current?.focus(); }, []);

    useEffect(() => {
        const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [cooldown]);

    if (!otpToken) return <Navigate to="/login" replace />;

    const expired = secondsLeft <= 0;
    const mmss = `${String(Math.floor(secondsLeft / 60)).padStart(2, "0")}:${String(secondsLeft % 60).padStart(2, "0")}`;

    const submit = async (e) => {
        e.preventDefault();
        if (code.length !== 6) return toast.error("Kode OTP harus 6 digit");
        setLoading(true);
        const res = await verifyOtp(otpToken, code);
        setLoading(false);
        if (!res.ok) return toast.error(res.error);
        toast.success("Login berhasil");
        if (res.user.must_change_password) navigate("/ubah-password");
        else navigate(halamanAwal(res.user.role));
    };

    const handleResend = async () => {
        setResending(true);
        const res = await resendOtp(otpToken);
        setResending(false);
        if (!res.ok) return toast.error(res.error);
        setOtpToken(res.otpToken);
        setCode("");
        setSecondsLeft(OTP_TTL_SECONDS);
        setCooldown(RESEND_COOLDOWN_SECONDS);
        toast.success("Kode OTP baru sudah dikirim ke email Anda");
        inputRef.current?.focus();
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                <div className="brand-gradient text-white px-8 py-6">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center border border-white/20">
                            <ShieldCheck className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-white font-semibold">Verifikasi 2 Langkah</div>
                            <h2 className="text-xl font-extrabold" style={{ fontFamily: "Plus Jakarta Sans" }}>
                                Masukkan Kode OTP
                            </h2>
                        </div>
                    </div>
                </div>

                <div className="px-8 py-6">
                    <p className="text-sm text-slate-600 mb-5">
                        Kode verifikasi 6 digit sudah dikirim ke email{email ? <> <span className="font-semibold text-slate-800">{email}</span></> : " terdaftar Anda"}.
                        Kode berlaku {Math.floor(OTP_TTL_SECONDS / 60)} menit.
                    </p>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label htmlFor="otp-code" className="text-slate-700 text-xs font-semibold uppercase tracking-wide">Kode OTP</Label>
                            <Input
                                id="otp-code"
                                ref={inputRef}
                                data-testid="otp-code-input"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                required
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                placeholder="123456"
                                className="mt-1.5 h-12 text-center text-2xl tracking-[0.5em] font-semibold"
                            />
                            <p className={`text-xs mt-1.5 ${expired ? "text-rose-600" : "text-slate-500"}`}>
                                {expired ? "Kode sudah kadaluarsa. Kirim ulang kode di bawah." : `Sisa waktu: ${mmss}`}
                            </p>
                        </div>

                        <Button
                            type="submit"
                            data-testid="otp-submit-button"
                            disabled={loading || expired || code.length !== 6}
                            className="w-full h-11 bg-[#00B9AD] hover:bg-[#60C0D0] text-[#0F2E30] font-semibold"
                        >
                            {loading ? "Memverifikasi..." : "Verifikasi & Masuk"}
                        </Button>
                    </form>

                    <div className="flex items-center justify-between mt-4 text-xs">
                        <button
                            type="button"
                            data-testid="otp-resend-button"
                            onClick={handleResend}
                            disabled={resending || cooldown > 0}
                            className="text-[#00877D] font-semibold disabled:text-slate-400 disabled:cursor-not-allowed hover:underline"
                        >
                            {resending ? "Mengirim..." : cooldown > 0 ? `Kirim ulang kode (${cooldown}s)` : "Kirim ulang kode"}
                        </button>
                        <button
                            type="button"
                            data-testid="otp-back-to-login-button"
                            onClick={() => navigate("/login")}
                            className="text-slate-500 hover:underline"
                        >
                            Kembali ke login
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
